import { z } from "zod";
import type { CreatePayRuleProfileInput } from "../schemas/pay-rules-api.js";
import type { OnboardingStatus } from "./index.js";

export const BETA_PAY_PERIOD_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "SEMIMONTHLY", label: "Semi-monthly" },
  { value: "MONTHLY", label: "Monthly" },
] as const;

export const BETA_CURRENCY_OPTIONS = [
  { value: "USD", label: "USD — US Dollar" },
  { value: "CAD", label: "CAD — Canadian Dollar" },
  { value: "EUR", label: "EUR — Euro" },
] as const;

export const BETA_SETUP_PATHS = ["blank", "import_csv", "add_manual"] as const;
export type BetaSetupPath = (typeof BETA_SETUP_PATHS)[number];

export const BETA_PAY_RULE_TEMPLATES = [
  "weekly_40h",
  "daily_ot",
  "weekend_holiday",
  "later",
] as const;
export type BetaPayRuleTemplate = (typeof BETA_PAY_RULE_TEMPLATES)[number];

export const DEFAULT_BETA_GEOFENCE_RADIUS_METERS = 250;

const betaSiteSchema = z
  .object({
    skip: z.boolean().default(false),
    name: z.string().trim().max(200).optional(),
    code: z
      .string()
      .trim()
      .max(32)
      .regex(/^[A-Za-z0-9-]*$/, "Code may only contain letters, numbers, and hyphens")
      .optional(),
    address: z.string().trim().max(500).optional(),
    geofenceEnabled: z.boolean().default(false),
    geofenceRadiusMeters: z.coerce
      .number()
      .int()
      .min(10)
      .max(50000)
      .default(DEFAULT_BETA_GEOFENCE_RADIUS_METERS),
  })
  .superRefine((data, ctx) => {
    if (data.skip) return;

    if (!data.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Site name is required",
        path: ["name"],
      });
    }
    if (!data.code?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Site code is required",
        path: ["code"],
      });
    }
  });

export const finishBetaOnboardingSchema = z.object({
  companyName: z.string().trim().min(2).max(120),
  timezone: z.string().trim().min(1).max(64),
  defaultCurrency: z.enum(["USD", "CAD", "EUR"]).default("USD"),
  payPeriodType: z.enum(["WEEKLY", "BIWEEKLY", "SEMIMONTHLY", "MONTHLY"]),
  site: betaSiteSchema.default({ skip: true }),
  setupPath: z.enum(BETA_SETUP_PATHS),
  payRuleTemplate: z.enum(BETA_PAY_RULE_TEMPLATES),
});

export type FinishBetaOnboardingInput = z.infer<typeof finishBetaOnboardingSchema>;

export const BETA_SETUP_PATH_LABELS: Record<BetaSetupPath, string> = {
  blank: "Start blank",
  import_csv: "Import workers CSV",
  add_manual: "Add workers manually",
};

export const BETA_PAY_RULE_TEMPLATE_LABELS: Record<
  Exclude<BetaPayRuleTemplate, "later">,
  string
> = {
  weekly_40h: "Weekly 40h overtime",
  daily_ot: "Daily OT + double time",
  weekend_holiday: "Weekend / holiday heavy",
};

export function buildStarterPayRuleProfile(
  template: Exclude<BetaPayRuleTemplate, "later">,
): CreatePayRuleProfileInput {
  switch (template) {
    case "weekly_40h":
      return {
        name: "Weekly 40h overtime",
        stackingMode: "HIGHEST_MULTIPLIER_WINS",
        rules: [
          {
            type: "WEEKLY_OT",
            thresholdHours: 40,
            multiplier: 1.5,
            priority: 10,
            active: true,
          },
        ],
      };
    case "daily_ot":
      return {
        name: "Daily OT + double time",
        stackingMode: "HIGHEST_MULTIPLIER_WINS",
        rules: [
          {
            type: "DAILY_OT",
            thresholdHours: 8,
            multiplier: 1.5,
            priority: 10,
            active: true,
          },
        ],
      };
    case "weekend_holiday":
      return {
        name: "Weekend / holiday heavy",
        stackingMode: "HIGHEST_MULTIPLIER_WINS",
        rules: [
          {
            type: "WEEKEND",
            multiplier: 1.5,
            priority: 10,
            active: true,
          },
          {
            type: "HOLIDAY",
            multiplier: 2,
            priority: 20,
            active: true,
          },
        ],
      };
  }
}

/** Daily OT template also needs double-time — inserted server-side (schema omits DAILY_DT). */
export function buildStarterPayRuleRows(
  template: Exclude<BetaPayRuleTemplate, "later">,
): Array<{
  type: string;
  thresholdHours?: number;
  multiplier?: number;
  priority: number;
  active: boolean;
}> {
  const profile = buildStarterPayRuleProfile(template);
  const rows = profile.rules.map((rule, index) => ({
    type: rule.type,
    thresholdHours: rule.thresholdHours,
    multiplier: rule.multiplier,
    priority: rule.priority ?? index * 10,
    active: rule.active ?? true,
  }));

  if (template === "daily_ot") {
    rows.push({
      type: "DAILY_DT",
      thresholdHours: 12,
      multiplier: 2,
      priority: 20,
      active: true,
    });
  }

  return rows;
}

export function resolveBetaOnboardingStatus(_input: FinishBetaOnboardingInput): OnboardingStatus {
  return "COMPLETE";
}
